import { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Paper, Alert, CircularProgress, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoginIcon from '@mui/icons-material/Login';
import { useCookies } from 'react-cookie';
import axiosFetch from '../../utils/axiosFetch';

const SalesforcePortal = () => {
  const scriptLoaded = useRef(false);
  const [cookies, , removeCookie] = useCookies(['jwtsalesforce']);
  const [frontdoorUrl, setFrontdoorUrl] = useState(null);
  const [appId, setAppId] = useState(null);
  const [lightningOutUrl, setLightningOutUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Fetch frontdoor URL with retry logic to handle cookie timing issues
    const fetchFrontdoorUrl = async (retryCount = 0, maxRetries = 3) => {
      try {
        setLoading(true);
        
        // Add a small initial delay to ensure cookies are set
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        const response = await axiosFetch.get('/auth/sso/salesforce/frontdoor');
        setFrontdoorUrl(response.data.frontdoorUrl.frontdoor_uri || response.data);
        setAppId(response.data.appId || null);
        setLightningOutUrl(response.data.lightningOutUrl || null);
        setIsAuthenticated(true);
        setError(null);
        setLoading(false);
      } catch (err) {
        if (err.response?.status === 401) {
          // Retry on 401 in case cookie hasn't been set yet
          if (retryCount < maxRetries) {
            const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 3000); // Exponential backoff, max 3s
            setTimeout(() => {
              fetchFrontdoorUrl(retryCount + 1, maxRetries);
            }, retryDelay);
            return;
          }
          // Remove invalid cookie after all retries exhausted
          removeCookie('jwtsalesforce', { path: '/', domain: '.usbank.com' });
          setIsAuthenticated(false);
          setError('Please log in to access Salesforce');
        } else {
          // Remove cookie on other errors as well
          removeCookie('jwtsalesforce', { path: '/', domain: '.usbank.com' });
          setError(err.message || 'Failed to load Salesforce portal');
        }
        setLoading(false);
      }
    };

    fetchFrontdoorUrl();
  }, []);

  useEffect(() => {
    // Load Lightning Out 2.0 script only when authenticated and expanded
    if (isAuthenticated && isExpanded && !scriptLoaded.current) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = lightningOutUrl;
      
      script.onload = () => {
        console.log('Lightning Out 2.0 script loaded');
      };

      script.onerror = () => {
        console.error('Failed to load Lightning Out 2.0 script');
        setError('Failed to load Salesforce components');
      };

      document.head.appendChild(script);
      scriptLoaded.current = true;
    }
  }, [isAuthenticated, isExpanded]);

  const handleLogin = () => {
    window.location.assign(
      `${
        process.env.NODE_ENV === "development"
          ? "http://localhost:8085"
          : process.env.NODE_ENV === "production"
          ? window.location.origin
          : window.location.origin
      }/api/auth/sso/salesforce/login`
    );
  };

  const handleExpandWidget = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Salesforce Portal...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {!isAuthenticated ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {error || 'Please log in to access the Salesforce portal'}
          </Alert>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
          >
            Login to Salesforce
          </Button>
        </Paper>
      ) : (
        <>
          <Button
            variant="outlined"
            startIcon={<ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />}
            onClick={handleExpandWidget}
            sx={{ mb: 2 }}
          >
            {isExpanded ? 'Collapse Incident Submit' : 'Submit a New Incident'}
          </Button>

          <Collapse in={isExpanded}>
            <Paper sx={{ p: 2, minHeight: '1000px' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Lightning Out 2.0 base configuration */}
              <lightning-out-application
                app-id={appId}
                frontdoor-url={frontdoorUrl}
                components="c-peru-web-to-case"
              />

              {/* Lightning Out 2.0 component */}
              <c-peru-web-to-case style={{height: '1000px'}} />
            </Paper>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default SalesforcePortal;